import { useEffect, useState } from 'react';
import { useCartStore } from '../store/cartStore';
import { storeService } from '../services/storeService';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Link, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/currency';
import { safeImageUrl } from '../utils/url';

function BackButton() {
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-2 text-sm font-medium text-text-medium hover:text-chocolate border border-champagne-dark hover:border-chocolate bg-white hover:bg-champagne px-4 py-2 rounded-full transition-all duration-150"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      Voltar para os produtos
    </Link>
  );
}

export function CartPage() {
  const { items, removeItem, updateQuantity, toggleGift, totalPrice, totalItems, clearCart } = useCartStore();
  const navigate = useNavigate();

  const [stockChecking, setStockChecking] = useState(true);
  const [removedItems, setRemovedItems] = useState<{ productName: string; variantLabel: string }[]>([]);
  const [reducedStockItems, setReducedStockItems] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    if (items.length === 0) {
      setStockChecking(false);
      return;
    }

    const uniqueProductIds = Array.from(new Set(items.map((i) => i.productId)));

    Promise.all(uniqueProductIds.map((id) => storeService.getProduct(id).catch(() => null))).then(
      (products) => {
        const removed: { productName: string; variantLabel: string }[] = [];
        const reduced = new Map<string, number>();

        const toRemove: { productId: number; variantId: number }[] = [];

        items.forEach((cartItem) => {
          const product = products.find((p) => p?.id === cartItem.productId);
          if (!product) return;
          const variant = product.variants.find((v) => v.id === cartItem.variantId);
          if (!variant) return;

          const key = `${cartItem.productId}-${cartItem.variantId}`;
          if (variant.stockQuantity === 0) {
            removed.push({ productName: cartItem.productName, variantLabel: cartItem.variantLabel });
            toRemove.push({ productId: cartItem.productId, variantId: cartItem.variantId });
          } else if (variant.stockQuantity < cartItem.quantity) {
            reduced.set(key, variant.stockQuantity);
          }
        });

        setRemovedItems(removed);
        setReducedStockItems(reduced);
        setStockChecking(false);

        if (toRemove.length > 0) {
          setTimeout(() => {
            toRemove.forEach(({ productId, variantId }) => removeItem(productId, variantId));
          }, 3000);
        }
      }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-4"><BackButton /></div>
        <EmptyState
          title="Carrinho vazio"
          description="Adicione produtos ao carrinho para continuar."
          action={<Link to="/"><Button>Ver produtos</Button></Link>}
        />
      </div>
    );
  }

  const itemCount = totalItems();

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-dark">Carrinho</h1>
          <div className="flex items-center gap-3 mt-0.5">
            <p className="text-sm text-text-light">{itemCount} {itemCount === 1 ? 'item' : 'itens'}</p>
            <button
              onClick={clearCart}
              className="text-sm text-danger hover:text-danger/80 transition-colors"
            >
              Esvaziar carrinho
            </button>
          </div>
        </div>
        <BackButton />
      </div>

      {stockChecking && (
        <p className="text-sm text-text-light text-center py-2">Verificando disponibilidade...</p>
      )}

      {!stockChecking && removedItems.length > 0 && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-2">
          <p className="text-sm font-semibold text-red-700 mb-1">
            {removedItems.length === 1 ? 'Item indisponível no estoque:' : 'Itens indisponíveis no estoque:'}
          </p>
          <ul className="flex flex-col gap-1">
            {removedItems.map((item, idx) => (
              <li key={idx} className="text-sm text-red-800">
                <span className="font-medium">{item.productName}</span> — {item.variantLabel}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!stockChecking && reducedStockItems.size > 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-2">
          <p className="text-sm font-semibold text-amber-700 mb-2">
            Quantidade ajustada por disponibilidade:
          </p>
          <div className="flex flex-col gap-2">
            {items
              .filter((i) => reducedStockItems.has(`${i.productId}-${i.variantId}`))
              .map((item) => {
                const available = reducedStockItems.get(
                  `${item.productId}-${item.variantId}`
                )!;
                return (
                  <div
                    key={`${item.productId}-${item.variantId}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {item.imageUrl && (
                        <img
                          src={safeImageUrl(item.imageUrl)}
                          alt={item.productName}
                          className="w-8 h-8 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <span className="text-sm text-amber-800 truncate">
                        <span className="font-medium">{item.productName}</span> —{' '}
                        {item.variantLabel}
                        <span className="text-xs ml-1">(disponível: {available})</span>
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        updateQuantity(item.productId, item.variantId, available);
                        setReducedStockItems((prev) => {
                          const m = new Map(prev);
                          m.delete(`${item.productId}-${item.variantId}`);
                          return m;
                        });
                      }}
                      className="text-xs font-medium text-amber-700 hover:text-amber-900 whitespace-nowrap underline"
                    >
                      Ajustar
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {items.map((item) => (
          <div key={`${item.productId}-${item.variantId}`} className="bg-white rounded-xl p-4 flex gap-4 shadow-sm">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-champagne-light flex-shrink-0">
              {item.imageUrl ? (
                <img src={safeImageUrl(item.imageUrl)} alt={item.productName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">👗</div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-medium text-text-dark text-base line-clamp-1">{item.productName}</p>
              <p className="text-text-light text-sm mt-0.5">{item.variantLabel}</p>
              {item.variantSku && (
                <p className="text-text-light text-xs mt-0.5 font-mono">SKU: {item.variantSku}</p>
              )}
              <p className="text-chocolate font-bold text-base mt-1">
                R$ {formatCurrency(item.promotionalPrice ?? item.price)}
              </p>
            </div>

            <div className="flex flex-col items-end gap-2">
              <button
                onClick={() => removeItem(item.productId, item.variantId)}
                className="text-text-light hover:text-danger text-sm transition-colors py-1"
              >
                Remover
              </button>
              <button
                onClick={() => toggleGift(item.productId, item.variantId)}
                aria-pressed={!!item.isGift}
                className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border-2 transition-all duration-150 ${
                  item.isGift
                    ? 'border-chocolate bg-champagne text-chocolate'
                    : 'border-champagne-dark bg-white text-text-medium hover:border-chocolate/50 hover:bg-champagne/40'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18C2.754 7.5 2.25 8.004 2.25 8.625v1.5c0 .621.504 1.125 1.125 1.125z" />
                </svg>
                {item.isGift ? 'É presente' : 'Marcar como presente'}
              </button>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => updateQuantity(item.productId, item.variantId, item.quantity - 1)}
                  aria-label="Diminuir quantidade"
                  className="w-11 h-11 rounded-lg border border-champagne-dark flex items-center justify-center text-text-medium hover:border-chocolate text-lg"
                >
                  −
                </button>
                <span className="text-base w-6 text-center">{item.quantity}</span>
                <button
                  onClick={() => updateQuantity(item.productId, item.variantId, item.quantity + 1)}
                  disabled={item.quantity >= item.stockQuantity}
                  aria-label="Aumentar quantidade"
                  className={`w-11 h-11 rounded-lg border flex items-center justify-center text-lg transition-colors ${
                    item.quantity >= item.stockQuantity
                      ? 'border-champagne-dark text-text-light opacity-40 cursor-not-allowed'
                      : 'border-champagne-dark text-text-medium hover:border-chocolate'
                  }`}
                >
                  +
                </button>
              </div>
              {item.quantity >= item.stockQuantity && (
                <span className="text-xs text-warning">Limite de estoque</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-white rounded-xl p-4 shadow-sm">
        <div className="flex justify-between items-center font-bold text-text-dark text-lg">
          <span>Total</span>
          <span>R$ {formatCurrency(totalPrice())}</span>
        </div>
        <Button
          fullWidth
          size="lg"
          className="mt-4"
          onClick={() => navigate('/checkout')}
          disabled={stockChecking}
        >
          Finalizar pedido
        </Button>
      </div>
    </div>
  );
}
